import { useCallback, useEffect, useRef, useState } from "react";
import { analyzeRun, createRun, getRun } from "./aiCompetitorAnalysisApi";
import { isInFlight, isTerminal } from "./helpers";

/** How often a generating run is re-read. */
const POLL_INTERVAL_MS = 4000;

/**
 * Drives one analysis from setup through to a finished result.
 *
 * @param {object|null} initialRun - The company's latest run, if it has one.
 * @returns {object} The run, in-flight flags, errors, and the two actions.
 *
 * @remarks
 * Polling is an interval on an external system, so it lives in an effect keyed
 * on whether the run is still working. A run already generating when the page
 * mounts is picked up the same way, which is what lets a reload resume rather
 * than orphan it.
 */
export function useRunAnalysis(initialRun) {
  const [run, setRun] = useState(initialRun);
  const [recommending, setRecommending] = useState(false);
  const [starting, setStarting] = useState(false);
  const [error, setError] = useState(null);
  const [forbidden, setForbidden] = useState(false);
  const timer = useRef(null);

  useEffect(() => {
    setRun(initialRun);
  }, [initialRun]);

  /**
   * Records a failed request, separating an authorisation refusal.
   *
   * @param {object} result - Envelope returned by the API layer.
   * @param {string} fallback - Message to show when the API sent none.
   */
  const handleFailure = useCallback((result, fallback) => {
    if (result.status === 403) {
      setForbidden(true);
      return;
    }
    setError(result.message || fallback);
  }, []);

  const startRecommendation = useCallback(
    async ({ sectorName, baseCompanyName, industryId }) => {
      setError(null);
      setRecommending(true);

      const result = await createRun({
        sectorName,
        baseCompanyName,
        industryId: industryId ?? null,
      });

      setRecommending(false);

      if (!result.isSuccess) {
        handleFailure(result, "Could not identify competitors. Please try again.");
        return;
      }
      setRun(result.data);
    },
    [handleFailure],
  );

  const startAnalysis = useCallback(
    async (selectedCompetitorIds) => {
      if (!run) return;
      setError(null);
      setStarting(true);

      const result = await analyzeRun(run.id, selectedCompetitorIds);

      setStarting(false);

      if (!result.isSuccess) {
        handleFailure(result, "Could not start the analysis. Please try again.");
        return;
      }
      setRun(result.data);
    },
    [run, handleFailure],
  );

  useEffect(() => {
    if (!isInFlight(run)) {
      return undefined;
    }

    const runId = run.id;
    let cancelled = false;

    const poll = async () => {
      const result = await getRun(runId);
      if (cancelled) return;

      if (result.isSuccess && result.data) {
        setRun(result.data);
        if (isTerminal(result.data)) return;
      }
      timer.current = setTimeout(poll, POLL_INTERVAL_MS);
    };

    timer.current = setTimeout(poll, POLL_INTERVAL_MS);

    return () => {
      cancelled = true;
      if (timer.current) clearTimeout(timer.current);
    };
  }, [run]);

  return {
    run,
    recommending,
    starting,
    analysing: isInFlight(run),
    error,
    forbidden,
    dismissForbidden: () => setForbidden(false),
    startRecommendation,
    startAnalysis,
    reset: () => setRun(null),
  };
}
